import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import ExternalLink from 'lucide-react-native/icons/external-link';
import MapPin from 'lucide-react-native/icons/map-pin';
import Star from 'lucide-react-native/icons/star';
import { ScrollView, StyleSheet, Text, View, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { BoatFacts } from '@/components/detail/boat-facts';
import { BoatCityLink } from '@/components/detail/boat-city-link';
import { BoatHead } from '@/components/detail/boat-head';
import { RelatedList } from '@/components/detail/related-list';
import { BoatCard } from '@/components/riviera/boat-card';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Fonts, MaxContentWidth, Radius, Spacing } from '@/constants/theme';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useRelatedBoats } from '@/hooks/use-related';
import { useTheme } from '@/hooks/use-theme';
import { useBoat } from '@todo/core/hooks/use-boat';
import { isBoatDelisted, type Boat } from '@todo/core/boats';
import { cities } from '@/lib/events-data';
import { localizedBoatDescription, localizedBoatTitle } from '@/lib/i18n/boat-content';
import { currencySymbol } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { getPrefetched } from '@todo/core/static-prefetch';

export { generateBoatStaticParams as generateStaticParams } from '@todo/core/detail/boat-detail';

type Translate = (key: string, options?: Record<string, unknown>) => string;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return '';
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

export default function BoatScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isLg } = useBreakpoint();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { boat: fetchedBoat, loading, error } = useBoat(id);

  const boat = fetchedBoat ?? getPrefetched<Boat>('boat', id) ?? null;

  const cityName = boat?.city_id
    ? (cities.find((c) => c.id === boat.city_id)?.name ?? boat.city_id)
    : null;
  const title = boat ? localizedBoatTitle(boat, locale) : '';
  const description = boat ? localizedBoatDescription(boat, locale) : null;

  const { related, relatedSeedKey, seeAllHref, cityId, hasCityPage, typePage } =
    useRelatedBoats(boat);
  const relatedHeading = cityId
    ? t('boats.moreInCity', { city: cityName })
    : t('boats.more');

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      <Stack.Screen options={{ headerShown: false }} />
      {boat ? (
        <BoatHead
          boat={boat}
          locale={locale}
          related={related}
          relatedSeedKey={relatedSeedKey}
          hasCityPage={hasCityPage}
          typePage={typePage}
        />
      ) : null}
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}>
        {boat ? (
          <>
          <View style={styles.centered}>
            <View style={[styles.page, isLg && styles.pageLg]}>
              <View style={[styles.heroBox, isLg && styles.heroBoxLg]}>
                {boat.image_url ? (
                  <Image source={{ uri: boat.image_url }} style={styles.hero} contentFit="cover" />
                ) : (
                  <View style={[styles.hero, { backgroundColor: theme.muted }]} />
                )}
                <LinearGradient
                  colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']}
                  style={styles.heroScrim}
                />
                <Pressable
                  accessibilityLabel={t('a11y.back')}
                  onPress={() => router.back()}
                  style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
                  <ArrowLeft size={20} color="#fff" />
                </Pressable>
                <View style={styles.heroBadge}>
                  <Badge variant="gold">{t('boats.badge')}</Badge>
                </View>
              </View>

              <View
                style={[
                  styles.card,
                  isLg && styles.cardLg,
                  { backgroundColor: theme.card, borderColor: theme.border },
                ]}>
                <Text
                  style={[
                    styles.title,
                    isLg && styles.titleLg,
                    { color: theme.text, fontFamily: Fonts?.serif },
                  ]}>
                  {title}
                </Text>

                <View style={styles.metaList}>
                  {boat.rating != null ? (
                    <View style={styles.metaRow}>
                      <Star size={16} color={theme.gold} fill={theme.gold} />
                      <Text style={[styles.metaText, { color: theme.text }]}>
                        {boat.rating.toFixed(1)}
                      </Text>
                    </View>
                  ) : null}
                  {cityName ? (
                    <View style={styles.metaRow}>
                      <MapPin size={16} color={theme.gold} />
                      <Text style={[styles.metaText, { color: theme.text }]}>{cityName}</Text>
                    </View>
                  ) : null}
                </View>

                {/* A delisted boat gets a notice instead of the price + Book
                    button — its booking link is likely dead (isBoatDelisted). */}
                {isBoatDelisted(boat) ? (
                  <View style={[styles.delistedNotice, { backgroundColor: theme.muted }]}>
                    <Text style={[styles.delistedBanner, { color: theme.text }]}>
                      {t('boats.delistedBanner')}
                    </Text>
                    <Text style={[styles.delistedHint, { color: theme.mutedForeground }]}>
                      {t('boats.delistedHint')}
                    </Text>
                  </View>
                ) : null}

                {!isBoatDelisted(boat) && boat.from_price != null ? (
                  <View style={styles.priceRow}>
                    <Text style={[styles.price, { color: theme.gold }]}>
                      {formatPrice(boat.from_price, boat.currency, t)}
                    </Text>
                    <Text style={[styles.perDay, { color: theme.mutedForeground }]}>
                      {t('boats.perDay')}
                    </Text>
                  </View>
                ) : null}

                {!isBoatDelisted(boat) && boat.booking_url ? (
                  <>
                    <Button
                      variant="gold"
                      fullWidth
                      style={styles.bookBtn}
                      leadingIcon={<ExternalLink size={18} color={theme.goldForeground} />}
                      onPress={() =>
                        openExternal(boat.booking_url as string, {
                          kind: 'boat',
                          source: boat.source,
                          id: boat.id,
                          title,
                          city: boat.city_id,
                          locale,
                        })
                      }>
                      {t('boats.book')}
                    </Button>
                    <Text style={[styles.via, { color: theme.mutedForeground }]}>
                      {t('boats.viaClickAndBoat')}
                    </Text>
                  </>
                ) : null}

                {description ? (
                  <>
                    <View style={[styles.divider, { backgroundColor: theme.border }]} />
                    <Text
                      style={[
                        styles.sectionHeading,
                        { color: theme.text, fontFamily: Fonts?.serif },
                      ]}>
                      {t('boats.aboutBoat')}
                    </Text>
                    <Text style={[styles.description, { color: theme.textSecondary }]}>
                      {description}
                    </Text>
                  </>
                ) : null}

                <View style={[styles.divider, { backgroundColor: theme.border }]} />
                <BoatFacts boat={boat} locale={locale} />
              </View>

              {hasCityPage && cityId ? (
                <View style={[styles.faqBox, isLg && styles.faqBoxLg]}>
                  <BoatCityLink cityId={cityId} locale={locale} typePage={typePage} />
                </View>
              ) : null}
            </View>
          </View>
          {related.length > 0 ? (
            <RelatedList heading={relatedHeading} seeAllHref={seeAllHref}>
              {related.map((r) => (
                <BoatCard key={r.id} boat={r} eager={false} />
              ))}
            </RelatedList>
          ) : null}
          </>
        ) : loading ? (
          <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
            {t('boats.loadingOne')}
          </Text>
        ) : error ? (
          <Text style={[styles.statusText, { color: theme.destructive }]}>
            {t('boats.loadError', { error })}
          </Text>
        ) : (
          <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
            {t('boats.notFound')}
          </Text>
        )}
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
  },
  pageLg: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.four,
  },
  heroBox: {
    width: '100%',
    aspectRatio: 16 / 9,
    position: 'relative',
  },
  heroBoxLg: {
    borderRadius: Radius['2xl'],
    overflow: 'hidden',
  },
  hero: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  card: {
    marginTop: -Spacing.four,
    borderTopLeftRadius: Radius['2xl'],
    borderTopRightRadius: Radius['2xl'],
    borderWidth: 1,
    borderBottomWidth: 0,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    paddingBottom: Spacing.five,
  },
  cardLg: {
    marginTop: -Spacing.five,
    borderRadius: Radius['2xl'],
    borderBottomWidth: 1,
    paddingHorizontal: Spacing.five,
    paddingVertical: Spacing.five,
    maxWidth: 760,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 33,
  },
  titleLg: {
    fontSize: 34,
    lineHeight: 41,
  },
  metaList: {
    marginTop: Spacing.three,
    gap: Spacing.two,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  metaText: {
    fontSize: 15,
    flex: 1,
  },
  priceRow: {
    marginTop: Spacing.three,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: Spacing.two,
  },
  price: {
    fontSize: 20,
    fontWeight: '700',
  },
  perDay: {
    fontSize: 13,
  },
  delistedNotice: {
    marginTop: Spacing.three,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    gap: 4,
  },
  delistedBanner: {
    fontSize: 15,
    fontWeight: '700',
  },
  delistedHint: {
    fontSize: 13,
  },
  bookBtn: {
    marginTop: Spacing.three,
  },
  via: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: Spacing.two,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
  sectionHeading: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: Spacing.two,
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
  },
  faqBox: {
    marginTop: Spacing.five,
    paddingHorizontal: Spacing.three,
  },
  faqBoxLg: {
    maxWidth: 760,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 0,
  },
});
